import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  FileText,
  FileUp,
  FolderOpen,
  Loader2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReasonLine } from "@/components/ui/disabled-reason";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useUploadStore, type FileMatch } from "@/stores/upload-store";
import { usePdfUpload } from "@/hooks/usePdfUpload";
import { useBibtexImport } from "@/hooks/useBibtexImport";
import { useBibtexImportStore } from "@/stores/bibtex-import-store";
import { usePdfPaperImport } from "@/hooks/usePdfPaperImport";
import { matchPdfToPapers } from "@/services/pdf-matcher";
import {
  extractTitleAndDoiFromPdf,
  extractTitleFromFilename,
} from "@/services/pdf-text-extractor";
import type { PaperWithRole } from "@/types/paper";
import { toast } from "@/lib/toast";
import {
  isRisText,
  isBibtexText,
  parseRis,
  extractTitleFromCitation,
} from "@/lib/ris-parser";

interface Props {
  open: boolean;
  onClose: () => void;
  papers: PaperWithRole[];
  conceptId: string;
}

export function BulkPdfImportDialog({
  open,
  onClose,
  papers,
  conceptId,
}: Props) {
  const {
    files,
    stage,
    uploadProgress,
    setFiles,
    setStage,
    updateFileMatch,
    markFileStatus,
    reset,
  } = useUploadStore();

  const { uploadPdfFast, extractAndSendText, invalidatePapers } = usePdfUpload(conceptId);
  const { uploadAndImport: uploadBibtex } = useBibtexImport(conceptId);
  const { createPapersFromPdfs } = usePdfPaperImport(conceptId);
  const bibtexStore = useBibtexImportStore();

  const [importRole, setImportRole] = useState<"foundational" | "trawled">("trawled");
  // Drag-over visual state for the "Select PDFs" drop zone.
  const [isDraggingPdf, setIsDraggingPdf] = useState(false);
  // "Paste references" (BibTeX / RIS / citation) — replaces the old toolbar text drop.
  const [pasteText, setPasteText] = useState("");
  const [pasteImporting, setPasteImporting] = useState(false);

  // Rights attestation — required before any uploads or paper creation happen.
  // Per spec §9 / backend enforces on /import/pdf-papers and /import/pdf-file.
  const [rightsAttested, setRightsAttested] = useState(false);

  // Track whether we're in "create papers" mode (no existing papers to match)
  const hasPapers = papers.length > 0;

  // Extracted metadata for "create papers" mode
  const [extractedMeta, setExtractedMeta] = useState<
    { title: string; doi: string | null; filename: string; selected: boolean }[]
  >([]);

  // All papers deduplicated (for matching — detects already-uploaded files)
  const allPapersDeduped = useMemo(() => {
    const seen = new Set<string>();
    return papers.filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [papers]);

  // Papers without PDFs (for the dropdown — only papers that need uploads)
  const candidatePapers = useMemo(
    () => allPapersDeduped.filter((p) => p.canonical_pdf_status !== "Success"),
    [allPapersDeduped],
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const bibInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef(false);

  function handleOpenChange(v: boolean) {
    if (!v) {
      abortRef.current = true;
      setExtractedMeta([]);
      reset();
      onClose();
    }
  }

  function handleFilesSelected(inputFiles: FileList | null) {
    const allFiles = Array.from(inputFiles ?? []);

    // Route .bib files to bibtex import
    const bibFile = allFiles.find((f) => f.name.toLowerCase().endsWith(".bib"));
    if (bibFile) {
      uploadBibtex(bibFile, importRole);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Filter to PDFs only (needed for folder scan which returns all file types)
    const pdfFiles = allFiles.filter(
      (f) => f.name.toLowerCase().endsWith(".pdf"),
    );
    if (pdfFiles.length === 0) return;

    const fileMatches: FileMatch[] = pdfFiles.map((file) => ({
      file,
      extractedTitle: null,
      matches: [],
      bestMatch: null,
      selectedPaperId: null,
      status: "pending" as const,
    }));

    setFiles(fileMatches);
    setStage("matching");

    // Reset inputs so same selection can be re-used
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
  }

  // Import pasted references (BibTeX / RIS / raw citation). Mirrors the
  // logic of the retired toolbar text-drop: route .bib to the bibtex importer,
  // parse RIS into paper entries, else extract a title and resolve via S2.
  async function handlePasteImport() {
    const text = pasteText.trim();
    if (!text) return;
    setPasteImporting(true);
    try {
      // BibTeX — reuse the .bib importer (shows the inline progress below).
      if (isBibtexText(text)) {
        const file = new File([text], "pasted.bib", { type: "text/plain" });
        uploadBibtex(file, importRole);
        setPasteText("");
        return;
      }

      // RIS — parse and create paper entries.
      if (isRisText(text)) {
        const entries = parseRis(text);
        if (entries.length === 0) {
          toast.error("Could not parse RIS data");
          return;
        }
        const res = await createPapersFromPdfs(
          entries.map((e) => ({
            title: e.title,
            doi: e.doi,
            authors: e.authors,
            year: e.year,
            filename: "",
          })),
          importRole,
        );
        toast.success(`Imported ${res.added} paper${res.added !== 1 ? "s" : ""}`, {
          description:
            res.duplicates > 0
              ? `${res.duplicates} duplicate${res.duplicates !== 1 ? "s" : ""} skipped`
              : undefined,
        });
        setPasteText("");
        return;
      }

      // Fallback — extract a title from the citation and resolve via S2.
      const title = extractTitleFromCitation(text);
      if (!title) {
        toast.error("Could not parse pasted text as references", {
          description: "For best results, set Zotero Quick Copy to BibTeX or RIS.",
        });
        return;
      }
      const res = await createPapersFromPdfs(
        [{ title, doi: null, authors: [], year: null, filename: "" }],
        importRole,
      );
      if (res.added > 0) {
        toast.success(`Added "${res.results[0]?.title ?? title}"`);
        setPasteText("");
      } else {
        toast.warning("Paper may already exist or could not be resolved");
      }
    } catch {
      toast.error("Failed to import references");
    } finally {
      setPasteImporting(false);
    }
  }

  // Run matching when we enter the matching stage
  useEffect(() => {
    if (stage !== "matching" || files.length === 0) return;

    let cancelled = false;

    if (!hasPapers) {
      // No papers to match — extract metadata only (concurrent)
      async function extractOnly() {
        const meta: { title: string; doi: string | null; filename: string; selected: boolean }[] = new Array(files.length);
        const CONCURRENCY = 4;
        let next = 0;

        async function worker() {
          while (next < files.length && !cancelled) {
            const i = next++;
            try {
              const { title, doi } = await extractTitleAndDoiFromPdf(files[i].file);
              const displayTitle = title || extractTitleFromFilename(files[i].file.name) || files[i].file.name;
              meta[i] = { title: displayTitle, doi: doi || null, filename: files[i].file.name, selected: true };
              updateFileMatch(i, { extractedTitle: displayTitle });
            } catch {
              const fallback = extractTitleFromFilename(files[i].file.name) || files[i].file.name;
              meta[i] = { title: fallback, doi: null, filename: files[i].file.name, selected: true };
              updateFileMatch(i, { extractedTitle: fallback });
            }
          }
        }

        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, files.length) }, () => worker()),
        );

        if (!cancelled) {
          setExtractedMeta(meta.filter(Boolean));
          setStage("review");
        }
      }
      extractOnly();
      return () => { cancelled = true; };
    }

    // Build a set of DOIs/titles for papers that already have PDFs — skip those early
    const alreadyHavePdfDois = new Set(
      allPapersDeduped
        .filter((p) => p.canonical_pdf_status === "Success" && p.doi)
        .map((p) => p.doi!.toLowerCase()),
    );

    async function matchOneFile(i: number) {
      if (cancelled) return;
      try {
        // Step 1: Try filename-based match first (instant, no pdf.js)
        const filenameTitle = extractTitleFromFilename(files[i].file.name);
        let fnMatches = filenameTitle
          ? matchPdfToPapers(filenameTitle, [], allPapersDeduped, 5)
          : [];

        // If filename gives a high-confidence match to a paper that already
        // has a PDF, skip pdf.js extraction entirely
        if (
          fnMatches[0]?.category === "high" &&
          fnMatches[0].paper.canonical_pdf_status === "Success"
        ) {
          if (cancelled) return;
          updateFileMatch(i, {
            extractedTitle: filenameTitle,
            matches: fnMatches,
            bestMatch: fnMatches[0],
            selectedPaperId: null,
          });
          return;
        }

        // Step 2: Single pdf.js load for both title and DOI
        const { title, doi } = await extractTitleAndDoiFromPdf(files[i].file);

        // If DOI matches a paper that already has a PDF, skip further matching
        if (doi && alreadyHavePdfDois.has(doi.toLowerCase())) {
          const doiMatches = matchPdfToPapers(title, [], allPapersDeduped, 5, doi);
          if (cancelled) return;
          updateFileMatch(i, {
            extractedTitle: title,
            matches: doiMatches,
            bestMatch: doiMatches[0] ?? null,
            selectedPaperId: null,
          });
          return;
        }

        // Step 3: Full matching — pdf-extracted title + DOI
        let matches = matchPdfToPapers(title, [], allPapersDeduped, 5, doi);

        // Fallback: if poor results, try filename-derived title with DOI
        if ((!matches[0] || matches[0].confidence < 60) && filenameTitle) {
          fnMatches = matchPdfToPapers(filenameTitle, [], allPapersDeduped, 5, doi);
          if (fnMatches[0] && (!matches[0] || fnMatches[0].confidence > matches[0].confidence)) {
            matches = fnMatches;
          }
        }

        const bestMatch = matches.length > 0 ? matches[0] : null;
        const bestCandidate = matches.find((m) => m.paper.canonical_pdf_status !== "Success");
        const autoSelect = bestCandidate?.category === "high" ? bestCandidate.paper.id : null;

        if (cancelled) return;
        updateFileMatch(i, {
          extractedTitle: title,
          matches,
          bestMatch,
          selectedPaperId: autoSelect,
        });
      } catch {
        if (cancelled) return;
        updateFileMatch(i, {
          extractedTitle: null,
          matches: [],
          bestMatch: null,
          selectedPaperId: null,
        });
      }
    }

    async function runMatching() {
      // Process 4 files concurrently for parallelism
      const CONCURRENCY = 4;
      let next = 0;

      async function worker() {
        while (next < files.length && !cancelled) {
          const idx = next++;
          await matchOneFile(idx);
        }
      }

      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, files.length) }, () => worker()),
      );

      if (!cancelled) {
        setStage("review");
      }
    }

    runMatching();
    return () => {
      cancelled = true;
    };
    // We only want this to run when stage changes to "matching"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Files whose best match is a paper that already has a PDF — skip in review.
  const isAlreadyUploaded = useCallback(
    (fm: FileMatch) =>
      fm.bestMatch != null &&
      fm.bestMatch.category !== "low" &&
      fm.bestMatch.paper.canonical_pdf_status === "Success",
    [],
  );
  const reviewFiles = useMemo(
    () => files.filter((fm) => !isAlreadyUploaded(fm)),
    [files, isAlreadyUploaded],
  );
  const skippedCount = files.length - reviewFiles.length;

  const selectedCount = hasPapers
    ? files.filter((f) => f.selectedPaperId || f.createAsNew).length
    : extractedMeta.filter((m) => m.selected).length;
  const importWhy = selectedCount === 0 ? "Select at least one file."
    : !rightsAttested ? "Tick the rights box above to continue." : null;

  // Concurrent upload worker — shared by both modes
  const UPLOAD_CONCURRENCY = 3;

  const runConcurrentUploads = useCallback(
    async (
      items: { fileIndex: number; paperId: string }[],
    ) => {
      const textExtractionQueue: { paperId: string; file: File }[] = [];
      let next = 0;

      async function worker() {
        while (next < items.length && !abortRef.current) {
          const idx = next++;
          const { fileIndex, paperId } = items[idx];
          markFileStatus(fileIndex, "uploading");
          try {
            // rightsAttested was gated by the dialog checkbox; forward it
            // so the backend's attestation requirement on confirm-upload
            // sees the user's consent.
            await uploadPdfFast(paperId, files[fileIndex].file, rightsAttested);
            markFileStatus(fileIndex, "done");
            textExtractionQueue.push({ paperId, file: files[fileIndex].file });
          } catch {
            markFileStatus(fileIndex, "error", "Upload failed");
          }
        }
      }

      await Promise.all(
        Array.from({ length: Math.min(UPLOAD_CONCURRENCY, items.length) }, () => worker()),
      );

      // Single cache invalidation after all uploads
      await invalidatePapers();

      // Deferred text extraction — fire and forget, don't block UI
      for (const { paperId, file } of textExtractionQueue) {
        extractAndSendText(paperId, file);
      }
    },
    [files, uploadPdfFast, extractAndSendText, invalidatePapers, markFileStatus, rightsAttested],
  );

  // Upload handler for "match to existing papers" mode (also handles "create as new")
  const handleStartUpload = useCallback(async () => {
    const matched = files
      .map((f, i) => ({ file: f, index: i }))
      .filter((f) => f.file.selectedPaperId)
      .map((f) => ({ fileIndex: f.index, paperId: f.file.selectedPaperId! }));

    const newPapers = files
      .map((f, i) => ({ file: f, index: i }))
      .filter((f) => f.file.createAsNew);

    const totalCount = matched.length + newPapers.length;
    if (totalCount === 0) return;

    setStage("uploading");
    useUploadStore.setState((s) => ({
      uploadProgress: { ...s.uploadProgress, total: totalCount },
    }));
    abortRef.current = false;

    // Upload matched files directly
    if (matched.length > 0) {
      await runConcurrentUploads(matched);
    }

    // Create new papers then upload their PDFs
    if (newPapers.length > 0) {
      try {
        const entries = newPapers.map((np) => ({
          title: np.file.extractedTitle || np.file.file.name.replace(/\.pdf$/i, ""),
          doi: null as string | null,
          authors: [] as string[],
          year: null as number | null,
          filename: np.file.file.name,
        }));
        const result = await createPapersFromPdfs(entries, importRole, rightsAttested);
        const resultMap = new Map(result.results.map((r) => [r.filename, r.paper_id]));
        const uploadItems: { fileIndex: number; paperId: string }[] = [];
        for (const np of newPapers) {
          const paperId = resultMap.get(np.file.file.name);
          if (!paperId) {
            markFileStatus(np.index, "error", "Paper creation failed");
            continue;
          }
          uploadItems.push({ fileIndex: np.index, paperId });
        }
        if (uploadItems.length > 0) {
          await runConcurrentUploads(uploadItems);
        }
      } catch (err) {
        for (const np of newPapers) {
          markFileStatus(np.index, "error", err instanceof Error ? err.message : "Import failed");
        }
      }
    }

    setStage("done");
  }, [files, setStage, runConcurrentUploads, createPapersFromPdfs, importRole, markFileStatus, rightsAttested]);

  // Upload handler for "create papers from PDFs" mode
  const handleCreateAndUpload = useCallback(async () => {
    const selected = extractedMeta
      .map((m, i) => ({ ...m, fileIndex: i }))
      .filter((m) => m.selected);

    if (selected.length === 0) return;

    setStage("uploading");
    useUploadStore.setState((s) => ({
      uploadProgress: { ...s.uploadProgress, total: selected.length },
    }));
    abortRef.current = false;

    try {
      // Step 1: Create paper entries from extracted metadata
      const entries = selected.map((m) => ({
        title: m.title,
        doi: m.doi,
        authors: [] as string[],
        year: null as number | null,
        filename: m.filename,
      }));

      const result = await createPapersFromPdfs(entries, importRole, rightsAttested);

      // Step 2: Upload PDFs to created papers concurrently
      const resultMap = new Map(result.results.map((r) => [r.filename, r.paper_id]));
      const uploadItems: { fileIndex: number; paperId: string }[] = [];

      for (const entry of selected) {
        const paperId = resultMap.get(entry.filename);
        if (!paperId) {
          markFileStatus(entry.fileIndex, "error", "Paper creation failed");
          continue;
        }
        uploadItems.push({ fileIndex: entry.fileIndex, paperId });
      }

      await runConcurrentUploads(uploadItems);
    } catch (err) {
      // If paper creation fails entirely, mark all as error
      for (const entry of selected) {
        markFileStatus(entry.fileIndex, "error",
          err instanceof Error ? err.message : "Import failed");
      }
    }

    setStage("done");
  }, [extractedMeta, files, createPapersFromPdfs, importRole, setStage, markFileStatus, runConcurrentUploads, rightsAttested]);

  const handleSelectPaper = useCallback(
    (fileIndex: number, paperId: string | null) => {
      updateFileMatch(fileIndex, { selectedPaperId: paperId });
    },
    [updateFileMatch],
  );

  const toggleExtractedSelection = useCallback(
    (index: number) => {
      setExtractedMeta((prev) =>
        prev.map((m, i) => (i === index ? { ...m, selected: !m.selected } : m)),
      );
    },
    [],
  );

  const percent =
    uploadProgress.total > 0
      ? Math.round((uploadProgress.completed / uploadProgress.total) * 100)
      : 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {stage === "idle" && "Import Papers"}
            {stage === "matching" && (hasPapers ? "Matching PDFs to Papers..." : "Extracting Metadata...")}
            {stage === "review" && (hasPapers ? "Review Matches" : "Review Papers")}
            {stage === "uploading" && "Importing..."}
            {stage === "done" && "Import Complete"}
          </DialogTitle>
          <DialogDescription>
            {stage === "idle" &&
              "Import papers from PDFs, a folder, a .bib file, or pasted references."}
            {stage === "review" && hasPapers &&
              `${reviewFiles.length} PDF${reviewFiles.length !== 1 ? "s" : ""} to review.${skippedCount > 0 ? ` ${skippedCount} skipped (already uploaded).` : ""}`}
            {stage === "review" && !hasPapers &&
              `${extractedMeta.length} PDF${extractedMeta.length !== 1 ? "s" : ""} — new paper entries will be created via Semantic Scholar lookup.`}
          </DialogDescription>
        </DialogHeader>

        {/* Stage: idle — file selection */}
        {stage === "idle" && (
          <div className="space-y-3 py-2">
            {/* Role selector */}
            <div className="space-y-1">
              <label className="text-xs font-medium">Import as</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="importRole"
                    value="trawled"
                    checked={importRole === "trawled"}
                    onChange={() => setImportRole("trawled")}
                    className="size-3.5"
                  />
                  Trawled papers
                </label>
                <label className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="importRole"
                    value="foundational"
                    checked={importRole === "foundational"}
                    onChange={() => setImportRole("foundational")}
                    className="size-3.5"
                  />
                  Seed papers
                </label>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              {/* Select files — also a drop zone */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setIsDraggingPdf(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!isDraggingPdf) setIsDraggingPdf(true);
                }}
                onDragLeave={(e) => {
                  // Ignore dragleave fired when moving onto a child element.
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                    setIsDraggingPdf(false);
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDraggingPdf(false);
                  handleFilesSelected(e.dataTransfer.files);
                }}
                className={`flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors ${
                  isDraggingPdf
                    ? "border-primary bg-primary/5 text-foreground"
                    : "border-muted-foreground/25 text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground"
                }`}
              >
                <FileUp className="size-7" />
                <span className="text-sm font-medium">Select PDFs</span>
                <span className="text-xs">
                  {isDraggingPdf ? "Drop to import" : "Drop PDFs here or click to select"}
                </span>
              </button>

              {/* Scan folder */}
              <button
                type="button"
                onClick={() => folderInputRef.current?.click()}
                className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 p-6 text-muted-foreground transition-colors hover:border-muted-foreground/50 hover:text-foreground"
              >
                <FolderOpen className="size-7" />
                <span className="text-sm font-medium">Scan Folder</span>
                <span className="text-xs">Recursively find PDFs</span>
              </button>

              {/* BibTeX file */}
              <button
                type="button"
                onClick={() => bibInputRef.current?.click()}
                className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 p-6 text-muted-foreground transition-colors hover:border-muted-foreground/50 hover:text-foreground"
              >
                <FileText className="size-7" />
                <span className="text-sm font-medium">BibTeX File</span>
                <span className="text-xs">Import from .bib</span>
              </button>
            </div>

            {/* Paste references (BibTeX / RIS / citation) */}
            <div className="space-y-1.5">
              <label className="text-xs font-medium">Or paste references</label>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder="Paste BibTeX, RIS, or a citation…"
                rows={3}
                className="w-full resize-y rounded-md border bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!pasteText.trim() || pasteImporting}
                  onClick={handlePasteImport}
                >
                  {pasteImporting ? "Importing…" : "Import references"}
                </Button>
              </div>
            </div>

            {/* BibTeX import progress (inline) */}
            {bibtexStore.isImporting && (
              <div className="flex items-center gap-2 rounded-md bg-blue-500/10 px-3 py-2 text-xs text-blue-600 dark:text-blue-400">
                <Loader2 className="size-3.5 animate-spin" />
                {bibtexStore.progress.statusMessage || "Importing BibTeX..."}
              </div>
            )}
            {bibtexStore.isCompleted && !bibtexStore.isImporting && (
              <div className="flex items-center gap-2 rounded-md bg-green-500/10 px-3 py-2 text-xs text-green-600 dark:text-green-400">
                <CheckCircle2 className="size-3.5" />
                {bibtexStore.progress.statusMessage}
              </div>
            )}

            <div className="rounded-md border border-border/50 bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground space-y-0.5">
              <p>PDFs create new paper entries (resolved via Semantic Scholar when possible), or match to existing papers if you have them.</p>
              <p>Tip: Point "Scan Folder" at ~/Zotero/storage/ to import all local Zotero PDFs.</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              multiple
              className="hidden"
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
            <input
              ref={bibInputRef}
              type="file"
              accept=".bib"
              className="hidden"
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
            <input
              ref={folderInputRef}
              type="file"
              // @ts-ignore webkitdirectory is non-standard but widely supported
              webkitdirectory=""
              className="hidden"
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
          </div>
        )}

        {/* Stage: matching — spinner */}
        {stage === "matching" && (
          <div className="flex flex-col items-center gap-3 py-8">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {hasPapers
                ? `Extracting titles and matching to papers (${files.filter((f) => f.extractedTitle !== null || f.matches.length > 0).length}/${files.length})...`
                : `Extracting metadata from PDFs (${files.filter((f) => f.extractedTitle !== null).length}/${files.length})...`
              }
            </p>
          </div>
        )}

        {/* Stage: review — match table (existing papers) or extraction results (no papers) */}
        {stage === "review" && (
          <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden space-y-2 pr-1">
            {hasPapers ? (
              files.map((fm, i) =>
                isAlreadyUploaded(fm) ? null : (
                  <MatchRow
                    key={i}
                    fileMatch={fm}
                    papers={candidatePapers}
                    onSelectPaper={(paperId) => handleSelectPaper(i, paperId)}
                    onCreateAsNew={(val) => updateFileMatch(i, { createAsNew: val, selectedPaperId: val ? null : fm.selectedPaperId })}
                  />
                ),
              )
            ) : (
              extractedMeta.map((meta, i) => (
                <div
                  key={i}
                  className="rounded-md border bg-background p-2.5 text-sm overflow-hidden"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={meta.selected}
                      onChange={() => toggleExtractedSelection(i)}
                      className="shrink-0"
                    />
                    <span className="min-w-0 flex-1 truncate font-medium" title={meta.filename}>
                      {meta.filename}
                    </span>
                    {meta.doi && (
                      <Badge variant="outline" className="shrink-0 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        DOI
                      </Badge>
                    )}
                  </div>
                  <p
                    className="mt-0.5 truncate pl-6 text-xs text-muted-foreground"
                    title={meta.title}
                  >
                    {meta.title}
                    {meta.doi && <span className="ml-2 text-[10px]">({meta.doi})</span>}
                  </p>
                </div>
              ))
            )}
          </div>
        )}

        {/* Stage: uploading — progress */}
        {stage === "uploading" && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between text-sm">
              <span>
                {hasPapers
                  ? `Uploading ${uploadProgress.completed} of ${uploadProgress.total}...`
                  : `Creating papers and uploading PDFs (${uploadProgress.completed}/${uploadProgress.total})...`
                }
              </span>
              <span className="text-muted-foreground">{percent}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${percent}%` }}
              />
            </div>
            {files
              .filter((f) => f.status === "uploading")
              .map((f, i) => (
                <p
                  key={i}
                  className="truncate text-xs text-muted-foreground"
                >
                  Uploading {f.file.name}...
                </p>
              ))}
          </div>
        )}

        {/* Stage: done — summary */}
        {stage === "done" && (
          <div className="flex flex-col items-center gap-3 py-8">
            <CheckCircle2 className="size-10 text-green-600 dark:text-green-400" />
            <div className="text-center text-sm">
              <p>
                <span className="font-medium">
                  {uploadProgress.succeeded}
                </span>{" "}
                PDF{uploadProgress.succeeded !== 1 ? "s" : ""} imported
              </p>
              {uploadProgress.failed > 0 && (
                <p className="text-destructive">
                  {uploadProgress.failed} failed
                </p>
              )}
            </div>
          </div>
        )}

        {/* Rights attestation — required before Import button enables in review stage. */}
        {stage === "review" && (
          <label className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={rightsAttested}
              onChange={(e) => setRightsAttested(e.target.checked)}
              className="mt-0.5 size-3.5 shrink-0"
            />
            <span>
              I confirm I have the right to process these PDFs under my own research
              access. My uploaded copies will stay private to my account and will not
              be shared with other users.
            </span>
          </label>
        )}

        <DialogFooter>
          {stage === "idle" && (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
          {stage === "review" && (
            <>
              {importWhy && <ReasonLine id="bulk-pdf-import-why" className="mr-auto self-center">{importWhy}</ReasonLine>}
              <Button
                variant="outline"
                onClick={() => {
                  setExtractedMeta([]);
                  reset();
                }}
              >
                Back
              </Button>
              <Button
                onClick={hasPapers ? handleStartUpload : handleCreateAndUpload}
                disabled={importWhy !== null}
                aria-describedby={importWhy ? "bulk-pdf-import-why" : undefined}
              >
                {hasPapers
                  ? `Import Selected (${selectedCount})`
                  : `Create ${selectedCount} Paper${selectedCount !== 1 ? "s" : ""} & Upload`
                }
              </Button>
            </>
          )}
          {stage === "done" && (
            <Button
              onClick={() => {
                setExtractedMeta([]);
                reset();
                onClose();
              }}
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MatchRow({
  fileMatch,
  papers,
  onSelectPaper,
  onCreateAsNew,
}: {
  fileMatch: FileMatch;
  papers: PaperWithRole[];
  onSelectPaper: (paperId: string | null) => void;
  onCreateAsNew: (createAsNew: boolean) => void;
}) {
  const { file, extractedTitle, selectedPaperId, createAsNew, status } = fileMatch;

  // Filter out papers that already have PDFs — only show uploadable candidates
  const candidateMatches = fileMatch.matches.filter(
    (m) => m.paper.canonical_pdf_status !== "Success",
  );
  const bestCandidate = candidateMatches[0] ?? null;

  const confidenceBadge = bestCandidate ? (
    <Badge
      variant="outline"
      className={
        bestCandidate.category === "high"
          ? "shrink-0 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
          : bestCandidate.category === "medium"
            ? "shrink-0 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
            : "shrink-0 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
      }
    >
      {bestCandidate.confidence}%
    </Badge>
  ) : null;

  const statusIcon =
    status === "done" ? (
      <CheckCircle2 className="size-4 shrink-0 text-green-500" />
    ) : status === "error" ? (
      <XCircle className="size-4 shrink-0 text-destructive" />
    ) : status === "uploading" ? (
      <Loader2 className="size-4 shrink-0 animate-spin" />
    ) : null;

  return (
    <div className="rounded-md border bg-background p-2.5 text-sm overflow-hidden">
      {/* Top row: checkbox + filename + confidence + status */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!!selectedPaperId || !!createAsNew}
          onChange={(e) => {
            if (e.target.checked) {
              if (bestCandidate) {
                onSelectPaper(bestCandidate.paper.id);
              } else {
                onCreateAsNew(true);
              }
            } else {
              onSelectPaper(null);
              onCreateAsNew(false);
            }
          }}
          disabled={status === "uploading" || status === "done"}
          className="shrink-0"
        />
        <span className="min-w-0 flex-1 truncate font-medium" title={file.name}>
          {file.name}
        </span>
        {confidenceBadge}
        {statusIcon}
      </div>

      {/* Extracted title */}
      {extractedTitle && (
        <p
          className="mt-0.5 truncate pl-6 text-xs text-muted-foreground"
          title={extractedTitle}
        >
          {extractedTitle}
        </p>
      )}

      {/* Paper selector */}
      <div className="mt-1.5 pl-6">
        <select
          className="w-full truncate rounded border bg-background px-2 py-1 text-xs"
          value={createAsNew ? "__new__" : (selectedPaperId ?? "")}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "__new__") {
              onSelectPaper(null);
              onCreateAsNew(true);
            } else {
              onCreateAsNew(false);
              onSelectPaper(val || null);
            }
          }}
          disabled={status === "uploading" || status === "done"}
        >
          <option value="">-- None --</option>
          <option value="__new__">+ Create as new paper</option>
          {candidateMatches.length > 0 && (
            <optgroup label="Matches">
              {candidateMatches.map((m) => (
                <option key={m.paper.id} value={m.paper.id}>
                  {m.paper.authors[0] ?? "?"} ({m.paper.year}){" "}
                  {m.paper.title.slice(0, 80)}
                </option>
              ))}
            </optgroup>
          )}
          <optgroup label="All papers">
            {papers
              .filter(
                (p) =>
                  !candidateMatches.some((m) => m.paper.id === p.id),
              )
              .slice(0, 50)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.authors[0] ?? "?"} ({p.year}) {p.title.slice(0, 80)}
                </option>
              ))}
          </optgroup>
        </select>
      </div>
    </div>
  );
}
